import axios from 'axios';
import { AppConfig, JWT } from '../config';
import {
    CONSENT_TEXT_ID,
    POLICY_FETCH_TIMEOUT_MS,
    POLICY_CACHE_KEY_PREFIX,
    toDocumentLocale,
} from '../config/consent';
import { resolveCurrentLangCode } from '../config/currentLang';
import { safeGetItem, safeSetItem } from './safeStorage';

// FEAT-052 (#493): 동의 모달에 띄울 방침 원문을 가져온다.
//
// **BE 를 거친다.** RAYConsent 의 문구 조회는 인증이 없어 프런트가 직접 부를 수도 있지만,
// 그러면 프런트가 테넌트(dev/prod)를 스스로 판정해야 한다. 판 번호는 테넌트마다 다르므로
// 프런트가 고른 테넌트와 BE 가 기록하는 테넌트가 어긋나는 순간 **화면에 보인 판과 기록된
// 판이 달라진다.** 테넌트를 아는 곳을 BE 한 군데로 모아 그 어긋남을 구조적으로 없앤다.
//
// **왜 런타임 조회인가** — 개인정보 처리방침 최종본이 2026-10-12 에 도착한다. 문구를 코드나
// 정적 파일에 두면 그날 코드 수정·빌드·배포를 해야 한다. 런타임으로 읽으면 문구만 등록하면 끝난다.
//
// **왜 폴백이 필요한가** — 로그인 직후 모달은 막히면 안 되는 흐름이다. 런타임 호출만 두면
// 조회가 실패할 때 로그인이 막힌다. 직전 성공분을 보관해 두고 그것으로 띄운다.
// 폴백에 쓴 **판 번호를 그대로 적재에 실어야** 화면과 기록이 어긋나지 않는다.

// RAYConsent 가 선언하는 두 형식. 선언이 없으면 평문이다.
export const TEXT_FORMAT_PLAIN = 'text/plain';
export const TEXT_FORMAT_HTML = 'text/html';

const cacheKey = (locale) => `${POLICY_CACHE_KEY_PREFIX}${CONSENT_TEXT_ID}:${locale}`;

const readCache = (locale) => {
    try {
        const raw = safeGetItem(cacheKey(locale));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        // 판 번호·원문·로케일이 모두 있어야 쓸 수 있다 — 하나라도 없으면 기록이 가리킬 판이 없다.
        if (!parsed || typeof parsed.text !== 'string' || !parsed.version || !parsed.locale) return null;
        return parsed;
    } catch (e) {
        return null;
    }
};

const writeCache = (locale, payload) => {
    try {
        safeSetItem(cacheKey(locale), JSON.stringify(payload));
    } catch (e) {
        // 보관 실패는 다음 조회가 네트워크를 타게 될 뿐이다 — 흐름을 막지 않는다.
    }
};

/**
 * 현재 이용자에게 보여줄 방침 문구를 가져온다. throw 하지 않는다.
 *
 * 성공: { text, version, locale, format, sha256, source }  (`source` = 'network' | 'cache')
 * 실패: { error: true, locale }
 */
export const loadConsentText = async () => {
    // 화면 언어(19종)를 실제 문서 로케일(ko/en)로 접는다. 기록에 실을 값도 이 값이다.
    const locale = toDocumentLocale(resolveCurrentLangCode());

    try {
        const res = await axios.get(`${AppConfig.API_SERVER_PATH}/consent/text`, {
            ...JWT(),
            params: { textId: CONSENT_TEXT_ID, locale },
            timeout: POLICY_FETCH_TIMEOUT_MS,
            // 302 를 따라가면 엉뚱한 페이지의 200 을 성공으로 읽는다(홈페이지 실측 사고).
            // 🔴 브라우저(XHR)에서는 이 옵션이 효과가 없다 — 리다이렉트는 브라우저가 따라간다.
            // 실제 방어는 아래 응답 검사(status·필수 필드)다. Node 실행(시험)에서만 의미가 있다.
            maxRedirects: 0,
        });

        const data = res?.data;
        if (data?.status !== 'success') {
            throw new Error(`consent text non-success: ${data?.errmsg || 'unknown'}`);
        }

        const body = data.data || {};
        const text = typeof body.text === 'string' ? body.text : '';
        const version = body.version ? String(body.version) : '';
        if (!text || !version) {
            // 판 번호 없는 문구로는 기록을 남길 수 없다 — 받은 것을 그대로 믿지 않는다.
            throw new Error('consent text missing text or version');
        }

        const payload = {
            text,
            version,
            locale: body.locale ? String(body.locale) : locale,
            // 형식이 틀리면 화면에서 바로 드러난다 — HTML 을 평문으로 그리면 `<p>` 가 그대로
            // 보이고, 평문을 HTML 로 그리면 줄바꿈이 사라져 한 덩어리가 된다. RAYConsent 는
            // 형식도 그 판의 일부로 보므로(형식만 바꿔도 판이 오른다) 받은 값을 그대로 쓴다.
            // 선언이 없으면 평문이 기본이다.
            format: body.format ? String(body.format) : TEXT_FORMAT_PLAIN,
            sha256: body.sha256 ? String(body.sha256) : '',
        };
        writeCache(locale, payload);
        return { ...payload, source: 'network' };
    } catch (err) {
        const cached = readCache(locale);
        // silent-drop 금지 — 폴백으로 내려간 사실이 관측돼야 한다.
        console.warn('[consentText] policy fetch failed', {
            handler: 'consentText.loadConsentText',
            textId: CONSENT_TEXT_ID,
            locale,
            reason: err?.message || 'unknown error',
            fallback: cached ? `cache(version=${cached.version})` : 'none',
        });
        if (cached) return { ...cached, source: 'cache' };
        return { error: true, locale };
    }
};

export default loadConsentText;
